import React from 'react';
import { Users, Target, DollarSign, TrendingUp } from 'lucide-react';
import { Lead, UserRole } from '../types';
import Tooltip from './Tooltip';

interface KPICardsProps {
  leads: Lead[];
  userRole?: UserRole;
}

export default function KPICards({ leads, userRole }: KPICardsProps) {
  const canViewFinancials = userRole !== 'warehouse';

  // Exclude archived leads from active KPI metrics
  const activeLeads = leads.filter((l) => !l.isArchived);

  // Aggregate stats in real-time
  const totalLeads = activeLeads.length;
  
  // Conversion Rate: Leads with status 'Won' / total active leads
  const wonLeadsCount = activeLeads.filter((l) => l.status === 'Won' || l.pipeline === 'Won').length;
  const conversionRate = totalLeads > 0 ? ((wonLeadsCount / totalLeads) * 100).toFixed(1) : '0.0';

  // Pipeline Value: Sum of deal values of active leads
  const totalPipelineVal = activeLeads.reduce((sum, l) => sum + (l.status !== 'Lost' ? (l.dealValue || l.estimatedDealValue || 0) : 0), 0);

  // Formatter for Currency
  const formatCurrency = (val: number) => {
    if (val >= 1000000) {
      return `$${(val / 1000000).toFixed(1)}M`;
    } else if (val >= 1000) {
      return `$${(val / 1000).toFixed(0)}k`;
    }
    return `$${val.toLocaleString()}`;
  };

  return (
    <div className={`grid grid-cols-1 ${canViewFinancials ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-5 mb-8`}>
      {/* Total Leads Card */}
      <div className="bg-white dark:bg-slate-950 rounded-xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col relative overflow-hidden hover:border-slate-350 dark:hover:border-slate-700 transition-colors group">
        <Users className="w-20 h-20 absolute -right-4 -bottom-4 text-slate-100 dark:text-slate-900/30 pointer-events-none group-hover:scale-105 transition-transform" />
        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 tracking-wider uppercase mb-1.5 flex items-center">
          Total Leads
          <Tooltip text="The total number of leads in your pipeline." />
        </span>
        <div className="flex items-end gap-3 z-10 relative mt-1">
          <span className="text-3xl font-black text-slate-800 dark:text-slate-100 tracking-tight leading-none">
            {totalLeads.toLocaleString()}
          </span>
          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-350 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/40 px-1.5 py-0.5 rounded flex items-center mb-1">
            <TrendingUp className="w-3 h-3 mr-1" /> Active
          </span>
        </div>
      </div>

      {/* Conversion Rate Card */}
      <div className="bg-white dark:bg-slate-950 rounded-xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col relative overflow-hidden hover:border-slate-350 dark:hover:border-slate-700 transition-colors group">
        <Target className="w-20 h-20 absolute -right-4 -bottom-4 text-slate-100 dark:text-slate-900/30 pointer-events-none group-hover:scale-105 transition-transform" />
        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 tracking-wider uppercase mb-1.5 flex items-center">
          Conversion Rate
          <Tooltip text="The percentage of leads successfully closed and marked as WON." />
        </span>
        <div className="flex items-end gap-3 z-10 relative mt-1">
          <span className="text-3xl font-black text-slate-800 dark:text-slate-100 tracking-tight leading-none">
            {conversionRate}%
          </span>
          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-350 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/40 px-1.5 py-0.5 rounded flex items-center mb-1">
            <TrendingUp className="w-3 h-3 mr-1" /> Real-time
          </span>
        </div>
      </div>

      {/* Pipeline Value Card - Only visible if canViewFinancials */}
      {canViewFinancials && (
        <div className="bg-white dark:bg-slate-950 rounded-xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col relative overflow-hidden hover:border-slate-350 dark:hover:border-slate-700 transition-colors group">
          <DollarSign className="w-20 h-20 absolute -right-4 -bottom-4 text-slate-100 dark:text-slate-900/30 pointer-events-none group-hover:scale-105 transition-transform" />
          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 tracking-wider uppercase mb-1.5 flex items-center">
            Pipeline Value
            <Tooltip text="The aggregated value of all non-lost lead deals currently in progression." />
          </span>
          <div className="flex items-end gap-3 z-10 relative mt-1">
            <span className="text-3xl font-black text-slate-800 dark:text-slate-100 tracking-tight leading-none">
              {formatCurrency(totalPipelineVal)}
            </span>
            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-350 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-1.5 py-0.5 rounded flex items-center mb-1">
              Active Est.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
